import {
  AutoComplete,
  Button,
  Drawer,
  Form,
  Input,
  Rate,
  Select,
  Spin,
} from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { authenticateToken } from "../../utils/auth";
import {
  getPage,
  getParamValue,
  checkStatesAndPushToHistory,
  DriverStatus,
} from "../../utils/index";
import Loading from "../common/Loader";
import PaginationTwo from "../common/PaginationTwo";
import TopHeading from "./TopHeading";
import { debounce } from "lodash";
import Empty from "../common/Empty";
import AddDriver from "./AddDriver";
import usePrevious from "../../hooks/PreviousState";

const { Option } = Select;

const DriverList = () => {
  const router = useLocation();
  const history = useHistory();
  const [status, setStatus] = useState(getParamValue(router.search, "status"));
  const [mobileNumber, setMobileNumber] = useState(
    getParamValue(router.search, "mobileNumber")
  );
  const [limit, setLimit] = useState(16);
  const [visible, setVisible] = useState(undefined);
  const [driverData, setDriverData] = useState({
    loading: false,
    data: null,
  });
  const [selecteDriverForEdit, setSelecteDriverForEdit] = useState({
    loading: false,
    data: null,
  });
  const [driversOptions, setDriversOptions] = useState({
    loading: false,
    list: null,
  });
  const [selectedDriver, setSelectedDriver] = useState(undefined);

  const fetchRef = useRef(null);

  const [form] = Form.useForm();
  const loc = useLocation();
  const page = getParamValue(loc.search, "page");
  const previousMobileNumber = usePrevious(mobileNumber);
  const previousStatus = usePrevious(status);

  window.onbeforeunload = () => {
    saveCurrentStatesInLocalStorage();
  };

  const getDrivers = useCallback(async () => {
    let pageReseted = false;
    if (localStorage.getItem("rememberDriverList") != null) {
      checkLocalStorageAndSetParams();
      localStorage.removeItem("rememberDriverList");
    } else {
      pageReseted = checkStatesAndPushToHistory(
        [previousMobileNumber, previousStatus],
        [mobileNumber, status],
        ["mobileNumber", "status"],
        history
      );
    }

    setDriverData({ loading: true, data: null });
    const encodedUri = `${process.env.REACT_APP_RIDER_API}`;
    axios
      .get(
        `${encodedUri}/admin/driver?` +
          (mobileNumber
            ? `&mobileNumberOrName=${mobileNumber
                ?.toString()
                .replace("+88", "")}`
            : ``) +
          (status ? `&status=${status}` : ``) +
          (page ? `&page=${pageReseted ? 0 : page || 0}` : ``) +
          `&limit=${limit || 16}`,
        {
          headers: {
            Authorization: `Bearer ${authenticateToken()}`,
          },
        }
      )
      .then((res) => {
        setDriverData({ loading: false, data: res.data });
      })
      .catch((err) => {
        setDriverData({ loading: false, data: [] });
        console.error("Drivers: Error", err);
      });
  }, [limit, mobileNumber, status, page]);

  const getDriversOptions = useCallback(
    async (mobileNumber) => {
      setDriversOptions({ loading: true, list: null });
      const encodedUri = `${process.env.REACT_APP_RIDER_API}`;
      axios
        .get(
          `${encodedUri}/admin/driver?` +
            (mobileNumber
              ? `&mobileNumberOrName=${mobileNumber?.replace("+88", "")}`
              : ``) +
            (status ? `&status=${status}` : ``) +
            `&page=${0}` +
            `&limit=${20}`,
          {
            headers: {
              Authorization: `Bearer ${authenticateToken()}`,
            },
          }
        )
        .then((res) => {
          setDriversOptions({
            loading: false,
            list: res.data?.drivers?.map((driver: any) => ({
              label: `${driver.mobileNumber.replace("+88", "")} - ${
                driver.name
              }`,
              value: driver.mobileNumber.replace("+88", ""),
            })),
          });
        })
        .catch((err) => {
          setDriversOptions({ loading: false, list: [] });
          console.error("Drivers: Error", err);
        });
    },
    [status]
  );

  useEffect(() => {
    getDrivers();
  }, [getDrivers]);

  // const getDriversOptionsDebounce = (mobileNumber) => {
  //   const fetcher = (mobileNumber) => {
  //     getDriversOptions(mobileNumber);
  //   };

  //   return debounce(fetcher, 0.8);
  // };

  const getDriversOptionsDebounce = React.useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setDriversOptions({ loading: false, list: null });

      if (fetchId !== fetchRef.current) {
        return;
      }

      getDriversOptions(value);
    };

    return debounce(loadOptions, 800);
  }, [getDriversOptions]);

  const reseAllFieldData = () => {
    setStatus("");
    setMobileNumber("");
    form.resetFields();
    history.replace(router.pathname);
    window.location.reload();
  };

  useEffect(() => {
    if (visible) {
      setSelectedDriver(undefined);
      setSelecteDriverForEdit(undefined);
    }
  }, [setSelectedDriver, setSelecteDriverForEdit, visible]);

  useEffect(() => {
    setSelectedDriver(undefined);
    setSelecteDriverForEdit(undefined);
  }, [visible]);

  const onClose = () => {
    setVisible(false);
    // visible(false);
    setSelecteDriverForEdit(null);
    setSelectedDriver(null);
    getDrivers();
  };

  function checkLocalStorageAndSetParams() {
    var retrievedObject = JSON.parse(
      localStorage.getItem("rememberDriverList")
    );
    console.log("rememberDriverList: ", retrievedObject);

    if (retrievedObject.mobileNumber)
      setMobileNumber(retrievedObject.mobileNumber);
    if (retrievedObject.status) setStatus(retrievedObject.status);
  }

  const saveCurrentStatesInLocalStorage = () => {
    localStorage.setItem(
      "rememberDriverList",
      JSON.stringify({
        mobileNumber: mobileNumber,
        status: status,
        page: page,
      })
    );
  };

  return (
    <React.Fragment>
      <TopHeading {...driverData?.data?.totalElements} refetch={getDrivers} />
      <section className="search_area">
        <Form form={form} className="search_form">
          <Form.Item name="search" initialValue={mobileNumber}>
            <AutoComplete
              style={{ width: 300 }}
              onSearch={getDriversOptionsDebounce}
              onSelect={(val) => setMobileNumber(val.toString())}
              options={driversOptions?.list}
              defaultActiveFirstOption={false}
              notFoundContent={
                driversOptions?.loading ? <Spin size="small" /> : null
              }
            >
              <Input.Search
                size="large"
                placeholder="Search by Phone (01...)"
                onSearch={(val) => setMobileNumber(val)}
                enterButton
                loading={driversOptions.loading}
                pattern={`[0-9]`}
                maxLength={11}
              />
            </AutoComplete>
          </Form.Item>

          <Form.Item initialValue={status}>
            <Select
              placeholder="Driver Status"
              onChange={(val) => setStatus(val)}
              value={status}
              style={{ minWidth: 250 }}
            >
              <Option value={""}>ALL</Option>
              {Object.values(DriverStatus)?.map((status, i) => (
                <Option value={status} key={i}>
                  {status?.split("_").join(" ") || "Status"}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        <div className="search_btn">
          <Button type="primary" danger size="large" onClick={reseAllFieldData}>
            Reset
          </Button>
        </div>
      </section>
      <div className="product-list-item-area white-bg section-padding-bottom">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="product-list-items-inner-content">
                <div className="single-product-wrapper">
                  <div className="single-product-heading">
                    <span>Full Name</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Mobile Number</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Due Balance</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Ratting</span>
                  </div>
                  <div className="single-product-heading">
                    <span>Status</span>
                  </div>
                  {/* <div className="single-product-heading">
                    <span>Action</span>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            {driverData?.loading ? (
              <Loading />
            ) : (
              <div className="col-lg-12 product-table">
                <div className="product-table-area">
                  {driverData?.data?.drivers?.length ? (
                    driverData?.data?.drivers?.map(
                      (driver: any, index: any) => (
                        <React.Fragment key={index}>
                          <Link
                            to={`/drivers/${driver?.id}/details`}
                            onClick={saveCurrentStatesInLocalStorage}
                          >
                            <div
                              className="single-product-wrapper"
                              // onClick={() => setSelectedDriver(driver)}
                            >
                              <div className="single-product">
                                <span className="name text-capitalize">
                                  {driver?.name}
                                </span>
                              </div>
                              <div className="single-product">
                                <span className="name">
                                  {driver?.mobileNumber?.split("+88")}
                                </span>
                              </div>
                              <div className="single-product">
                                <span className="name">
                                  &#2547;{driver?.balance || 0}
                                </span>
                              </div>

                              <div className="single-product">
                                <span className="name d-flex-l">
                                  {driver?.numberOfRating ? (
                                    <Rate
                                      value={driver?.rating || 0}
                                      disabled
                                    />
                                  ) : (
                                    <Rate value={0} disabled />
                                  )}

                                  <small className="ml-1">
                                    ({driver?.numberOfRating || 0})
                                  </small>
                                </span>
                              </div>
                              <div className="single-product">
                                <span
                                  className={`radius-btn text-nowrap ${driver?.status?.toLowerCase()}`}
                                >
                                  {driver?.status?.split("_").join(" ")}
                                </span>
                              </div>
                              {/* <div className="single-product">
                              <button
                                className="btn btn-sm btn-warning mx-1"
                                onClick={() => setSelecteDriverForEdit(driver)}
                              >
                                <EditOutlined />
                              </button>
                            </div> */}
                            </div>
                          </Link>
                        </React.Fragment>
                      )
                    )
                  ) : (
                    <Empty data="Drivers" />
                  )}
                </div>
              </div>
            )}
          </div>

          <Drawer
            title="Edit Delivery Hero"
            width={selecteDriverForEdit ? 600 : 800}
            onClose={onClose}
            visible={selectedDriver || selecteDriverForEdit}
            bodyStyle={{ paddingBottom: 80 }}
            footer={
              <div
                style={{
                  textAlign: "right",
                }}
              ></div>
            }
          >
            <AddDriver
              visibleData={selecteDriverForEdit}
              onCloseMethod={onClose}
            />
          </Drawer>

          <PaginationTwo
            {...driverData?.data}
            limit={limit}
            page={getPage(loc.search)}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default DriverList;
